"use client";

import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useCallback,
  Suspense,
  Fragment,
  lazy,
  type ComponentType,
} from "react";
import { usePathname } from "next/navigation";
import { useTheme } from "@/providers/theme";
import { getPortalSession } from "@/app/actions";
import BootOverlay from "@/components/boot-overlay";
import { LIBUM_AVATAR_NAMES } from "@/lib/constants";

import {
  AccountReadinessContext,
  useAccountReadiness,
} from "./account-readiness";
import { AccountRailTransition } from "@/components/account-rail-transition";
import { CachedAccountRail } from "@/components/docs/cached-rail";
import {
  readAccountRail,
  writeAccountRail,
  type AccountRailSnapshot,
} from "@/lib/account-rail-cache";

import { DocsSearchStateProvider } from "./docs-search-state";

const GuestAvatarContext = createContext<string | null>(null);
export function useDocsGuestAvatar() {
  return useContext(GuestAvatarContext);
}

type BuilderFactory =
  typeof import("@/lib/spacetime").buildPortalSpacetimeBuilder;
export const AccountConnectionBuilderContext = createContext(
  (token: string | null, factory: BuilderFactory) => factory(token),
);

type Session = Awaited<ReturnType<typeof getPortalSession>>;
export const SessionContext = createContext<{
  session: Session | null;
  resolving: boolean;
  updateSession: (session: Session) => void;
}>({ session: null, resolving: true, updateSession: () => {} });
export function useDocsAccountPending() {
  const { session } = useContext(SessionContext);
  // Also retain the pending rail while the authenticated controls load their data.
  return !!session?.token;
}
export function useDocsHasSession() {
  return !!useContext(SessionContext).session?.token;
}
const AccountRuntime = lazy(() => import("@/providers/account-runtime"));
export const AccountRuntimeContext = createContext<ComponentType<{
  placement: "sidebar" | "header";
  fallback: React.ReactNode;
}> | null>(null);
export function useAccountSession() {
  return useContext(SessionContext);
}

/** Root-owned account state survives public/protected layout changes. Guests
 * render public content immediately without loading the realtime runtime. */
export function AccountSessionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const builder = useRef<{
    token: string | null;
    value: ReturnType<BuilderFactory>;
  } | null>(null);
  const getBuilder = useCallback(
    (token: string | null, factory: BuilderFactory) => {
      if (!builder.current || builder.current.token !== token)
        builder.current = { token, value: factory(token) };
      return builder.current.value;
    },
    [],
  );
  const pathname = usePathname();
  const isProtected = ["/settings", "/poweron-pro", "/glaci", "/insights"].some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  const [accountReady, setAccountReady] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [resolving, setResolving] = useState(true);
  const [guestAvatar, setGuestAvatar] = useState<string | null>(null);
  const { setFollowSystem } = useTheme();
  const signedIn = !!session?.token;
  const updateSession = useCallback((next: Session) => setSession(next), []);
  useEffect(() => {
    if (resolving) return;
    setFollowSystem(!signedIn);
    try {
      localStorage.setItem(
        "portal-theme-session",
        signedIn ? "signed-in" : "guest",
      );
    } catch {
      // The theme still works when browser storage is unavailable.
    }
    // Preserve the current mode during navigation while the next session resolves.
  }, [resolving, signedIn, setFollowSystem]);

  useEffect(() => {
    setGuestAvatar(
      LIBUM_AVATAR_NAMES[Math.floor(Math.random() * LIBUM_AVATAR_NAMES.length)],
    );
  }, []);
  useEffect(() => {
    let active = true;
    let pending = false;
    let refreshAgain = false;
    const refresh = async () => {
      if (pending) {
        refreshAgain = true;
        return;
      }
      pending = true;
      let confirmed = false;
      try {
        const next = await getPortalSession();
        confirmed = true;
        if (active) setSession(next);
      } catch {
        // Account availability must never block public documentation.
        // A failed request is not a sign-out. Retain a verified session.
      } finally {
        if (active && confirmed) setResolving(false);
        pending = false;
        if (active && refreshAgain) {
          refreshAgain = false;
          void refresh();
        }
      }
    };
    void refresh();
    window.addEventListener("focus", refresh);
    window.addEventListener("portal-session-changed", refresh);
    return () => {
      active = false;
      window.removeEventListener("focus", refresh);
      window.removeEventListener("portal-session-changed", refresh);
    };
  }, []);
  return (
    <SessionContext.Provider value={{ session, resolving, updateSession }}>
      <AccountConnectionBuilderContext.Provider value={getBuilder}>
        <DocsSearchStateProvider>
          <GuestAvatarContext.Provider value={guestAvatar}>
            <AccountReadinessContext.Provider value={accountReady}>
              {/* Public layouts never move beneath the asynchronously loaded runtime.
              Only their account controls subscribe; connecting cannot remount
              the document, navigation, or open property panels. */}
              <Fragment key="public-layout">
                {!isProtected && children}
              </Fragment>
              {session && (session.token || isProtected) && (
                <Suspense key="account-runtime" fallback={null}>
                  <AccountRuntime onReady={setAccountReady}>
                    {isProtected ? children : null}
                  </AccountRuntime>
                </Suspense>
              )}
              {isProtected && <BootOverlay key="boot-overlay" />}
            </AccountReadinessContext.Provider>
          </GuestAvatarContext.Provider>
        </DocsSearchStateProvider>
      </AccountConnectionBuilderContext.Provider>
    </SessionContext.Provider>
  );
}

function DocsAccountControlsContent({
  children,
  placement = "sidebar",
}: {
  children: React.ReactNode;
  placement?: "sidebar" | "header";
}) {
  const { session, resolving } = useContext(SessionContext);
  const AccountControls = useContext(AccountRuntimeContext);
  const accountReady = useAccountReadiness();
  const [snapshot, setSnapshot] = useState<AccountRailSnapshot | null>(null);
  const [hydrated, setHydrated] = useState(false);
  useLayoutEffect(() => {
    setSnapshot(readAccountRail());
    setHydrated(true);
    const refreshSnapshot = () => {
      const next = readAccountRail();
      // Keep the outgoing appearance until the confirmed guest transition finishes.
      if (next) setSnapshot(next);
    };
    window.addEventListener("portal-account-rail-changed", refreshSnapshot);
    return () =>
      window.removeEventListener(
        "portal-account-rail-changed",
        refreshSnapshot,
      );
  }, []);
  const confirmedGuest = !resolving && !session?.token;
  const [showCached, setShowCached] = useState(true);
  useEffect(() => {
    if (confirmedGuest) {
      setSnapshot((current) => current ?? readAccountRail());
      writeAccountRail(null);
      setShowCached(false);
    }
  }, [confirmedGuest]);
  const live = !!session?.token && !!AccountControls && accountReady;
  if (placement === "header") {
    return live && AccountControls ? (
      <AccountControls placement={placement} fallback={children} />
    ) : (
      <>{children}</>
    );
  }
  if (!hydrated)
    return (
      <div className="rail-bootstrap flex h-full w-12 shrink-0 md:w-[calc(var(--sidebar-width-icon)_+_1px)]">
        <div className="rail-bootstrap-cached h-full w-full">
          <CachedAccountRail
            bootstrap
            snapshot={{
              insights: true,
              upgrade: true,
              avatar: null,
              organizationLogo: null,
            }}
          />
        </div>
        <div className="rail-bootstrap-guest flex h-full w-full">
          {children}
        </div>
      </div>
    );
  return (
    <AccountRailTransition
      ready={live || (!!snapshot && showCached)}
      fallback={children}
    >
      {live && AccountControls ? (
        <AccountControls placement={placement} fallback={children} />
      ) : snapshot ? (
        <CachedAccountRail snapshot={snapshot} />
      ) : null}
    </AccountRailTransition>
  );
}

/** Public layouts keep account subscriptions inside the account slot. The SDK's
 * ConnectionManager shares the connection between these providers. Protected
 * layouts already supply the runtime and do not need another provider. */
export function DocsAccountControls(props: {
  children: React.ReactNode;
  placement?: "sidebar" | "header";
}) {
  const { session } = useAccountSession();
  const runtime = useContext(AccountRuntimeContext);
  const content = <DocsAccountControlsContent {...props} />;
  if (runtime || !session?.token) return content;
  return (
    <Suspense fallback={content}>
      <AccountRuntime manageSession={false}>{content}</AccountRuntime>
    </Suspense>
  );
}
